import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import posthog from "posthog-js";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import TrialMembershipModal, {
  TRIAL_MEMBERSHIP_POPUPS_ENABLED,
} from "@/components/TrialMembershipModal";
import {
  clearPendingCheckout,
  dismissPendingCheckout,
  readPendingCheckout,
} from "@/lib/pendingCheckout";
import UpgradeModal from "@/components/UpgradeModal";

function isMusicVideoProjectPath(path: string) {
  const pathname = path.split(/[?#]/)[0] || "/";
  const segments = pathname.split("/").filter(Boolean);
  const projectRouteIndex = segments.indexOf("ai-music-video");
  if (projectRouteIndex >= 0) {
    return Boolean(segments[projectRouteIndex + 1]);
  }
  const legacyProjectRouteIndex = segments.indexOf("ai-music-video-generator");
  return (
    legacyProjectRouteIndex >= 0 &&
    Boolean(segments[legacyProjectRouteIndex + 1])
  );
}

/**
 * In-app abandoned-checkout recovery: if this browser started a Stripe
 * checkout in the last 7 days and never reached /checkout/success, offer a
 * one-click way back in. Complements the server-side recovery email.
 *
 * While the $9.90 trial is live (VITE_TRIAL_MEMBERSHIP_POPUPS=true) the banner
 * opens TrialMembershipModal first so the trial mechanics ($9.90 for 7 days,
 * then Plus Monthly at $35/mo) are disclosed before payment. While the trial
 * is paused it recovers the abandoned checkout straight into the normal plans
 * (UpgradeModal) instead — no trial offered anywhere on web.
 */
export default function ResumeCheckoutBanner() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const [pending] = useState(() => readPendingCheckout());
  const [hidden, setHidden] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const shownTrackedRef = useRef(false);

  // Don't float over the pages that already are the destination.
  const onCheckoutSurface =
    location.includes("/subscription") || location.includes("/checkout");
  const onMusicVideoProject = isMusicVideoProjectPath(location);

  const eligible =
    !!pending &&
    !pending.dismissedAt &&
    isAuthenticated &&
    !onCheckoutSurface &&
    onMusicVideoProject;

  const { data: subscription } = trpc.payment.getSubscription.useQuery(
    undefined,
    { enabled: eligible },
  );
  const hasActivePaidSub =
    !!subscription &&
    subscription.plan !== "free" &&
    subscription.status === "active";

  // They purchased (this session or elsewhere) — retire the pending record.
  useEffect(() => {
    if (eligible && hasActivePaidSub) clearPendingCheckout();
  }, [eligible, hasActivePaidSub]);

  const visible = eligible && !hidden && !!subscription && !hasActivePaidSub;

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      posthog.capture("resume_checkout_banner_shown", {
        plan: pending?.planId ?? pending?.kind,
      });
    }
  }, [visible, pending]);

  if (!pending) return null;

  const handleResume = () => {
    posthog.capture("resume_checkout_banner_clicked", {
      plan: pending.planId ?? pending.kind,
      offer: TRIAL_MEMBERSHIP_POPUPS_ENABLED ? "plus_trial" : "plans",
    });
    if (TRIAL_MEMBERSHIP_POPUPS_ENABLED) {
      // Trial live: show the $9.90 offer first regardless of which plan the
      // pending checkout record points at.
      setShowTrialModal(true);
      return;
    }
    // Trial paused: recover straight into the normal plans.
    setHidden(true);
    setShowUpgradeModal(true);
  };

  const handleViewPlans = () => {
    posthog.capture("resume_checkout_banner_view_plans", {
      plan: pending.planId ?? pending.kind,
    });
    // Keep the pending record — they're comparing, not declining.
    setHidden(true);
    setShowTrialModal(false);
    setShowUpgradeModal(true);
  };

  const handleDismiss = () => {
    posthog.capture("resume_checkout_banner_dismissed", {
      plan: pending.planId ?? pending.kind,
    });
    dismissPendingCheckout();
    setHidden(true);
  };

  return (
    <>
      {visible && !showUpgradeModal && (
        <div className="fixed bottom-20 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[600px] -translate-x-1/2 lg:bottom-6">
          <div
            className="relative overflow-hidden rounded-[14px]"
            style={{
              background:
                "linear-gradient(150deg, #12131c 0%, #0c0d15 55%, #14101e 100%)",
              border: "1px solid rgba(255,255,255,0.07)",
              boxShadow:
                "0 24px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            {/* Ambient magenta glow */}
            <div
              className="resume-banner-glow pointer-events-none absolute"
              style={{
                top: "-60px",
                right: "120px",
                width: "260px",
                height: "180px",
                background:
                  "radial-gradient(circle, rgba(217,70,239,0.28), transparent 68%)",
                filter: "blur(20px)",
              }}
            />

            <button
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="absolute right-3 top-3 z-[2] flex h-[30px] w-[30px] items-center justify-center rounded-full text-[#5b6272] transition-colors hover:bg-white/[0.08] hover:text-[#d1d5e0]"
            >
              <X className="h-4 w-4" strokeWidth={2.2} />
            </button>

            <div className="relative flex items-center gap-[14px] py-[14px] pl-4 pr-11">
              {/* Copy */}
              <div className="min-w-0 flex-1">
                <h2
                  className="m-0 mb-1 text-[15px] font-bold text-white"
                  style={{ letterSpacing: "-0.01em" }}
                >
                  Your music video is waiting
                </h2>
                <p
                  className="m-0 mb-[6px] text-[12px] text-[#9ca3b4]"
                  style={{ lineHeight: 1.4 }}
                >
                  {TRIAL_MEMBERSHIP_POPUPS_ENABLED ? (
                    <>
                      <strong className="font-semibold text-[#d1d5e0]">
                        $9.90
                      </strong>{" "}
                      starts your first week of Plus with 500 credits
                    </>
                  ) : (
                    "Pick up where you left off and finish it without the watermark"
                  )}
                </p>
                {TRIAL_MEMBERSHIP_POPUPS_ENABLED && (
                  <button
                    onClick={handleViewPlans}
                    className="text-[12px] font-medium text-[#8a92a6] transition-colors hover:text-white"
                  >
                    View all plans
                  </button>
                )}
              </div>

              {/* Action */}
              <div className="flex shrink-0 items-center pr-1.5">
                <button
                  onClick={handleResume}
                  className="resume-banner-cta relative cursor-pointer whitespace-nowrap rounded-full px-5 py-2.5 text-[13px] font-bold text-white transition-transform hover:-translate-y-px active:translate-y-0"
                  style={{
                    background:
                      "linear-gradient(90deg, #ec4899, #d946ef, #a855f7, #ec4899)",
                    backgroundSize: "200% 100%",
                    boxShadow:
                      "0 12px 30px -8px rgba(217,70,239,0.65), inset 0 1px 0 rgba(255,255,255,0.3)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {TRIAL_MEMBERSHIP_POPUPS_ENABLED
                    ? "Start $9.90 trial"
                    : "Finish your video"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <TrialMembershipModal
        open={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        onViewPlans={handleViewPlans}
        trigger="resume_checkout_banner"
        variant="resume_claim"
      />
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        description="Choose a plan to finish your music video without watermark."
        trigger="resume_checkout_banner"
        defaultTab="yearly"
      />
    </>
  );
}
